import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import type { IntentStatus, TaskDTO } from "@roomate/core";
import { keys, useActors, useIntent, usePatch, usePost, useProjectArchived } from "../api.ts";
import { ProjectArchivedNotice } from "../components/ProjectArchivedNotice.tsx";
import { Action, Avatar, Bento, Check, Disclosure, Page, PageHeader, Pill, Ring, Tile } from "../calm.tsx";
import { IdeaStatus, useIdeaRow } from "../components/IdeaStatus.tsx";
import { ActorName, cx, ErrorNote, inputCls, Markdown, STAGE_LABEL } from "../ui.tsx";

const STATUS_TONE: Record<IntentStatus, "neutral" | "live" | "signal" | "danger" | "done"> = {
  draft: "neutral",
  spec_review: "signal",
  approved: "live",
  done: "done",
  cancelled: "danger",
};

const STAGE_TONE: Record<string, "neutral" | "live" | "signal" | "danger" | "done"> = {
  backlog: "neutral",
  ready: "neutral",
  in_progress: "live",
  blocked: "signal",
  verifying: "live",
  review: "signal",
  done: "done",
  cancelled: "neutral",
};

export function IntentPage() {
  const { id = "" } = useParams();
  const q = useIntent(id);
  const actors = useActors().data;
  const save = usePatch([keys.intent(id)]);
  const submit = usePost([keys.intent(id), ["decisions"]]);
  const ideaRow = useIdeaRow(id).row;
  const archived = useProjectArchived(q.data?.intent.projectId ?? "");
  const [spec, setSpec] = useState("");
  const [problem, setProblem] = useState("");
  const [criteria, setCriteria] = useState("");

  useEffect(() => {
    if (!q.data) return;
    setSpec(q.data.intent.specMd);
    setProblem(q.data.intent.problem);
    setCriteria(q.data.intent.acceptanceCriteria.map((c) => c.text).join("\n"));
  }, [q.data?.intent.updatedAt]);

  if (q.error) return <ErrorNote error={q.error} />;
  if (!q.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );
  const { intent, tasks, decisions } = q.data;
  const editable = intent.status === "draft";
  const lines = criteria
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const pending = decisions.find((d) => d.status === "open");
  const creator = actors?.find((a) => a.id === intent.createdBy);
  const doneTasks = tasks.filter((t) => t.stage === "done").length;
  const headline = editable
    ? "Draft. Needs a problem, spec and acceptance criteria."
    : pending
      ? intent.status === "approved" || intent.status === "done"
        ? "Waiting on a person."
        : "Waiting on a person to approve the spec."
      : intent.status === "spec_review"
        ? "Submitted, waiting on approval."
        : intent.status === "cancelled"
          ? "Cancelled."
          : intent.status === "done"
            ? "Done."
            : tasks.length === 0
              ? "Approved. No tasks yet."
              : doneTasks === tasks.length
                ? "Shipped. Every task is done."
                : "Approved. Agents are building it.";

  return (
    <Page wide>
      <PageHeader crumbs={[{ label: "Project", to: `/projects/${intent.projectId}` }]} title={intent.title} status={<Pill tone={STATUS_TONE[intent.status]}>{intent.status.replace("_", " ")}</Pill>} />
      <ProjectArchivedNotice projectId={intent.projectId} />

      <Bento className="mt-8">
        <Tile span={4} className="flex items-center gap-6">
          <Ring value={doneTasks} total={tasks.length} tone={tasks.length > 0 && doneTasks === tasks.length ? "ok" : "ink"} />
          <div className="min-w-0">
            <p className="text-[19px] font-medium leading-snug tracking-[-0.01em]">{headline}</p>
            <p className="mt-1.5 text-[13.5px] text-ink-2">{tasks.length ? `${doneTasks} of ${tasks.length} tasks done` : "No tasks yet"}</p>
          </div>
        </Tile>

        <Tile span={2} title="Filed by">
          <div className="flex items-center gap-3">
            {creator ? <Avatar name={creator.name} kind={creator.kind} size={36} /> : <span className="h-9 w-9 rounded-full border-2 border-dashed border-rule" />}
            <div className="min-w-0">
              <p className="truncate text-[15px] font-medium">{creator?.name ?? "Unknown"}</p>
              <p className="text-[12.5px] text-ink-3 capitalize">{intent.kind}</p>
            </div>
          </div>
        </Tile>

        {ideaRow && (
          <Tile span={6} title="What happens next" testId="intent-idea-status">
            <IdeaStatus row={ideaRow} compact />
          </Tile>
        )}

        {pending && (
          <Tile span={6} className="!bg-signal-soft/70">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[13px] text-ink-2">Waiting on a person</p>
                <p className="mt-1.5 text-[17px] leading-snug">
                  {pending.question} <span className="text-[14px] text-ink-2">· for <ActorName id={pending.assigneeId} actors={actors} /></span>
                </p>
              </div>
              <Link to="/" className="shrink-0 rounded-full bg-ink px-5 py-2.5 text-[14px] text-paper">
                Answer on Home
              </Link>
            </div>
          </Tile>
        )}

        {editable ? (
          <Tile span={6} title="Draft">
            <div className="space-y-5">
              <LabeledField label="Problem">
                <textarea className={inputCls} rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} />
              </LabeledField>
              <LabeledField label="Spec" hint="Markdown. This is where human time has the most leverage.">
                <textarea className={cx(inputCls, "font-mono text-[12.5px]")} rows={10} value={spec} onChange={(e) => setSpec(e.target.value)} />
              </LabeledField>
              <LabeledField label="Acceptance criteria" hint="one per line; verification checks every one">
                <textarea className={cx(inputCls, "font-mono text-[12.5px]")} rows={4} value={criteria} onChange={(e) => setCriteria(e.target.value)} />
              </LabeledField>
              <div className="flex gap-2">
                <Action onClick={() => save.mutate({ path: `/intents/${id}`, body: { problem, specMd: spec, acceptanceCriteria: lines } })} disabled={save.isPending}>
                  Save draft
                </Action>
                <Action
                  primary
                  testId="intent-submit"
                  disabled={archived || !spec.trim() || lines.length === 0 || submit.isPending}
                  onClick={async () => {
                    await save.mutateAsync({ path: `/intents/${id}`, body: { problem, specMd: spec, acceptanceCriteria: lines } });
                    submit.mutate({ path: `/intents/${id}/submit` });
                  }}
                >
                  Submit for approval
                </Action>
              </div>
              <ErrorNote error={save.error ?? submit.error} />
            </div>
          </Tile>
        ) : (
          <>
            <Tile span={4} title="Spec">
              <Clamp>
                {intent.problem && <Markdown text={intent.problem} className="mb-3 text-[14px] text-ink-2" />}
                <Markdown text={intent.specMd || "_no spec_"} className="text-[14.5px] leading-relaxed" />
              </Clamp>
            </Tile>
            <Tile span={2} title="Acceptance criteria">
              {intent.acceptanceCriteria.length === 0 ? (
                <p className="text-[13.5px] text-ink-3">None yet.</p>
              ) : (
                <ul className="divide-y divide-rule/60">
                  {intent.acceptanceCriteria.map((c) => (
                    <Check key={c.id} state="pending">
                      {c.text}
                    </Check>
                  ))}
                </ul>
              )}
            </Tile>
          </>
        )}

        <Tile span={6} title="Tasks" aside={tasks.length > 0 ? `${tasks.length}` : undefined}>
          {tasks.length === 0 ? (
            <p className="text-[13.5px] text-ink-3">No tasks yet — agents split the spec with create_tasks.</p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {tasks.slice(0, 3).map((t) => (
                  <TaskCard key={t.id} task={t} />
                ))}
              </div>
              {tasks.length > 3 && (
                <Disclosure summary={`${tasks.length - 3} more task${tasks.length - 3 === 1 ? "" : "s"}`} className="mt-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    {tasks.slice(3).map((t) => (
                      <TaskCard key={t.id} task={t} />
                    ))}
                  </div>
                </Disclosure>
              )}
            </>
          )}
        </Tile>
      </Bento>
    </Page>
  );
}

/** A bento card for one task: stage and risk read at a glance, the title does the rest. */
function TaskCard({ task: t }: { task: TaskDTO }) {
  return (
    <Link to={`/tasks/${t.id}`} className="block rounded-2xl bg-ink/[0.03] p-4 hover:bg-ink/[0.06]" data-testid="intent-task">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] text-ink-3">#{t.number}</span>
        <Pill tone={STAGE_TONE[t.stage] ?? "neutral"}>{STAGE_LABEL[t.stage]}</Pill>
      </div>
      <p className="mt-2 text-[14.5px] font-medium leading-snug">{t.title}</p>
      <p
        className={cx(
          "mt-2 text-[12.5px] capitalize",
          t.risk.tier === "low" ? "text-[color:var(--ok)]" : t.risk.tier === "medium" ? "text-[color:var(--medium)]" : t.risk.tier === "high" ? "text-high" : "font-medium text-critical",
        )}
      >
        {t.risk.tier} risk
      </p>
    </Link>
  );
}

/** A quiet label above a field: no mono, no uppercase, matches the Tile header style. */
function LabeledField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-ink-2">{label}</span>
      {hint && <span className="ml-2 text-[12.5px] text-ink-3">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

/** Long specs start folded to a few lines; the rest is one click away. */
function Clamp({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className={cx("relative overflow-hidden", !open && "max-h-[11em]")}>
        {children}
        {!open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[color:var(--card)] to-transparent" />}
      </div>
      <button onClick={() => setOpen((o) => !o)} className="mt-2 text-[13px] text-ink-2 hover:text-ink">
        {open ? "Show less" : "Read more"}
      </button>
    </div>
  );
}
